export type EnvironmentKind = "production" | "test" | "demo";

export interface DemoTarget {
  name: "dimensional" | "pchart" | "job_serial" | "cmm";
  stationId: string;
  modelId: string;
  characteristicId: string;
}

export interface DemoAuthorization {
  environmentId: string;
  tenantId: string;
  policyVersion: number;
  keyId: string;
  expiresAt: string;
  targets: DemoTarget[];
  signature: string;
}

export interface EnvironmentConfig {
  id: string;
  displayName: string;
  kind: EnvironmentKind;
  apiBaseUrl: string;
  tenantId?: string;
  demoAuthorization?: DemoAuthorization;
}

export interface DemoAuthorizationVerifier {
  verify(environment: EnvironmentConfig): Promise<boolean>;
}

export interface BootstrapPolicy {
  mode: "production" | "development";
  approvedDeployment: { id: string; apiBaseUrl: string } | null;
  approvedDeployments?: { id: string; apiBaseUrl: string; kind: "production" | "test" }[];
}

function record(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) throw new Error("Invalid deployment configuration");
  return value as Record<string, unknown>;
}

function requiredText(value: unknown): string {
  if (typeof value !== "string" || !value.trim() || value !== value.trim()) throw new Error("Invalid deployment identity");
  return value;
}

function endpoint(value: unknown, production: boolean): string {
  const raw = requiredText(value);
  if (!/^https?:\/\//i.test(raw) || /[\\\s]/.test(raw)) throw new Error("Invalid deployment endpoint");
  const url = new URL(raw);
  const host = url.hostname.toLowerCase().replace(/\.$/, "");
  const loopback = host === "localhost" || host.endsWith(".localhost")
    || /^(127|0)\./.test(host) || host === "[::1]" || host === "[::]"
    || /^\[::ffff:(7f[0-9a-f]{2}:|0:)/.test(host);
  if (url.username || url.password || url.search || url.hash
    || (production && (url.protocol !== "https:" || loopback))) throw new Error("Unapproved deployment endpoint");
  return url.href.replace(/\/$/, "");
}

/** Build-pinned target and explicit manifest selection must agree before auth loads. */
export function validateStartupEnvironment(value: unknown, policy: BootstrapPolicy): EnvironmentConfig {
  if (policy.mode !== "production" && policy.mode !== "development") throw new Error("Invalid bootstrap mode");
  const manifest = record(value);
  if (manifest.version !== 1) throw new Error("Unsupported environment manifest version");
  const selectedId = requiredText(manifest.selectedEnvironmentId);
  if (!Array.isArray(manifest.environments) || !manifest.environments.length) throw new Error("No deployment environments");
  const entries = manifest.environments.map(record);
  const ids = entries.map((entry) => requiredText(entry.id));
  if (new Set(ids).size !== ids.length) throw new Error("Ambiguous deployment selection");
  const selected = entries.find((entry) => entry.id === selectedId);
  const approved = policy.approvedDeployments?.find(item => item.id === selectedId) ?? policy.approvedDeployment;
  if (!selected || !approved) throw new Error("Deployment is not approved");
  const production = policy.mode === "production";
  const kind = production && "kind" in approved ? approved.kind : production ? "production" : "test";
  if (selected.kind !== kind) throw new Error("Deployment mode mismatch");
  const id = requiredText(selected.id);
  const displayName = requiredText(selected.displayName);
  const apiBaseUrl = endpoint(selected.apiBaseUrl, production);
  if (id !== requiredText(approved.id)
    || apiBaseUrl !== endpoint(approved.apiBaseUrl, production)) throw new Error("Deployment target mismatch");
  return { id, displayName, kind: kind as "production" | "test", apiBaseUrl };
}

export async function loadStartupEnvironment(policy: BootstrapPolicy, signal?: AbortSignal): Promise<EnvironmentConfig> {
  const response = await fetch("/environments.json", { cache: "no-store", redirect: "error", signal });
  if (!response.ok) throw new Error("Environment manifest unavailable");
  const manifest = await response.json();
  // Native launch accepts only an ID from the packaged approval registry, never a URL.
  const { isTauri, invoke } = await import("@tauri-apps/api/core");
  if (isTauri()) {
    const selected = await invoke<string | null>("read_environment_selection");
    if (selected !== null) manifest.selectedEnvironmentId = selected;
  }
  return validateStartupEnvironment(manifest, policy);
}

export class EnvironmentStore {
  private revisionValue = 0;
  private listeners = new Set<(environment: EnvironmentConfig, revision: number) => void>();

  constructor(private environmentValue: EnvironmentConfig) {}

  get current(): EnvironmentConfig { return this.environmentValue; }
  get revision(): number { return this.revisionValue; }

  switchTo(environment: EnvironmentConfig): void {
    if (environment.id === this.environmentValue.id && environment.apiBaseUrl === this.environmentValue.apiBaseUrl) return;
    this.environmentValue = environment;
    this.revisionValue += 1;
    this.listeners.forEach((listener) => listener(environment, this.revisionValue));
  }

  subscribe(listener: (environment: EnvironmentConfig, revision: number) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
}

export class DemoAccessDeniedError extends Error {
  constructor(message = "Demo scenario execution is not authorized for this environment") {
    super(message);
    this.name = "DemoAccessDeniedError";
  }
}

export class DemoExecutionGuard {
  constructor(
    private readonly environments: EnvironmentStore,
    private readonly verifier: DemoAuthorizationVerifier,
  ) {}

  captureRevision(): number { return this.environments.revision; }

  async assertAllowed(expectedRevision = this.environments.revision): Promise<DemoAuthorization> {
    const environment = this.environments.current;
    const authorization = environment.demoAuthorization;
    if (this.environments.revision !== expectedRevision) throw new DemoAccessDeniedError("Environment changed during demo execution");
    if (environment.kind !== "demo" || !environment.tenantId || !authorization) throw new DemoAccessDeniedError();
    if (authorization.environmentId !== environment.id || authorization.tenantId !== environment.tenantId) throw new DemoAccessDeniedError("Demo authorization target mismatch");
    if (Date.parse(authorization.expiresAt) <= Date.now()) throw new DemoAccessDeniedError("Demo authorization expired");
    if (!(await this.verifier.verify(environment))) throw new DemoAccessDeniedError("Demo authorization signature was not accepted");
    return authorization;
  }
}
